import * as React from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { Badge, Button, Icon, MediaPreview, SegmentedControl, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { useGenerateStoryboard } from "@/lib/queries/opportunities.queries"
import {
  jumpToTaskTargetWhenReady,
  TASK_SECTION_SEARCH_PARAM,
} from "@/features/task-progress/lib/task-section"
import { AnimaticPlayer } from "../AnimaticPlayer/AnimaticPlayer"
import { StoryboardArtDirectionNote } from "./StoryboardArtDirectionNote"
import type { ScriptDto } from "@/lib/queries"
import type { ScriptScene, StoryboardPanel, StoryboardVariant } from "@/features/opportunities/api/opportunities.api"

export type ScriptStoryboardModel = {
  id: string
  hook: string | null
  scenes: ScriptScene[]
  storyboardStatus: ScriptDto["storyboardStatus"]
  storyboardImages: ScriptDto["storyboardImages"]
  storyboardPanelStatus: ScriptDto["storyboardPanelStatus"]
  storyboardPanels: StoryboardPanel[]
}

interface ScriptStoryboardProps {
  script: ScriptDto
  setId: string
  tenantId: string
}

export interface ScriptStoryboardViewProps {
  script: ScriptStoryboardModel
  generate: (variant: StoryboardVariant) => void
  isPending: boolean
  /**
   * The hub script drawer renders the sketch action in its header. This view
   * then hides that button and treats `sketchPending` as the in-flight sketch run,
   * because the header mutation is a separate hook instance.
   */
  sketchGenerateExternal?: boolean
  sketchPending?: boolean
  labelClassName?: string
  /** Persona script details start folded so a ready storyboard does not shift the body. */
  defaultCollapsed?: boolean
}

/**
 * Collapsible in-card storyboard section. Owns the storyboard generate action
 * (single source of truth) for both variants:
 *
 * - `sketch` — the black-and-white sheet holding every panel in one image
 * - `panels` — photoreal frames, one per beat, played back as an animatic
 *
 * The two are generated and stored independently, so each has its own status and
 * either can be absent while the other is ready. Within a variant the same four
 * states apply: idle → generate button, generating → placeholder, ready → the
 * images, failed → inline error.
 *
 * Opportunities scripts default to expanded so a freshly-completed storyboard is
 * visible immediately. Persona details pass `defaultCollapsed`.
 */
export function ScriptStoryboard({ script, setId, tenantId }: ScriptStoryboardProps) {
  const { mutate, isPending } = useGenerateStoryboard(setId)
  return (
    <ScriptStoryboardView
      script={script}
      isPending={isPending}
      generate={(variant) => mutate({ scriptId: script.id, tenantId, variant })}
    />
  )
}

export function ScriptStoryboardView({
  script,
  generate: generateVariant,
  isPending,
  sketchGenerateExternal = false,
  sketchPending = false,
  labelClassName,
  defaultCollapsed = false,
}: ScriptStoryboardViewProps) {
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed)

  const sketchStatus = script.storyboardStatus
  const panelStatus = script.storyboardPanelStatus
  const images = script.storyboardImages ?? []
  const panels = script.storyboardPanels ?? []

  // Show whichever variant has something to show, preferring the sketch when both do.
  const [variant, setVariant] = React.useState<StoryboardVariant>(() =>
    panelStatus !== "NONE" && sketchStatus === "NONE" ? "panels" : "sketch",
  )
  // Which variant the in-flight mutation is for, so a run on one variant does not
  // render the other as busy.
  const [pendingVariant, setPendingVariant] = React.useState<StoryboardVariant>("sketch")

  const generate = React.useCallback(
    (target: StoryboardVariant) => {
      setPendingVariant(target)
      setVariant(target)
      generateVariant(target)
    },
    [generateVariant],
  )

  // The expanded animatic modal is URL state (`?storyboard=<scriptId>`), so an
  // open player can be shared as a link and survives a refresh.
  const navigate = useNavigate()
  const sectionRef = React.useRef<HTMLElement | null>(null)
  const {
    storyboard: expandedStoryboardScriptId,
    scriptId: searchScriptId,
    variant: searchVariant,
  } = useSearch({ strict: false }) as {
    storyboard?: string
    scriptId?: string
    variant?: StoryboardVariant
  }
  const isAnimaticExpanded = expandedStoryboardScriptId === script.id

  // Each script starts at its own default. The drawer reuses this view when the
  // selection changes, so a previously opened storyboard must not stay open.
  React.useEffect(() => {
    setCollapsed(defaultCollapsed)
  }, [defaultCollapsed, script.id])

  // A shared link must land on the animatic, not whichever variant the local
  // default picked from the generation statuses.
  React.useEffect(() => {
    if (isAnimaticExpanded) setVariant("panels")
  }, [isAnimaticExpanded])

  // Sole owner of Sketch / Animatic deep-link scroll: seed the toggle, wait
  // until this block is visible (parent CollapsibleReveal has opened), scroll
  // into the nested results scroller, then clear one-shot params so a later
  // card click can re-trigger.
  React.useEffect(() => {
    if (searchScriptId !== script.id) return
    if (searchVariant !== "sketch" && searchVariant !== "panels") return
    setVariant(searchVariant)
    setCollapsed(false)
    const jump = jumpToTaskTargetWhenReady(
      () => sectionRef.current,
      () => {
        void navigate({
          to: ".",
          search: (prev) => ({
            ...prev,
            variant: undefined,
            [TASK_SECTION_SEARCH_PARAM]: undefined,
          }),
          replace: true,
        })
      },
    )
    return jump.cancel
  }, [navigate, script.id, searchScriptId, searchVariant])

  const handleAnimaticExpandedChange = React.useCallback(
    (expanded: boolean) => {
      void navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          // `scriptId` is the existing deep-link param — it makes the shared URL
          // auto-expand this script's group and row underneath the modal.
          ...(expanded ? { scriptId: script.id } : {}),
          storyboard: expanded ? script.id : undefined,
        }),
        replace: !expanded,
      })
    },
    [navigate, script.id],
  )

  const activeStatus = variant === "panels" ? panelStatus : sketchStatus
  const isSketchBusy = sketchPending || (isPending && pendingVariant === "sketch")
  const isActivePending = variant === "sketch" ? isSketchBusy : isPending && pendingVariant === "panels"
  const hideInlineSketchGenerate = sketchGenerateExternal && variant === "sketch"
  const isGenerating = activeStatus === "GENERATING" || isActivePending
  // A regeneration keeps the previous result on screen under a loading overlay,
  // so the user never loses what they had while the new version is produced.
  const hasPrevious = variant === "panels" ? panels.length > 0 : images.length > 0
  const isFailed = activeStatus === "FAILED"
  const showPrevious = hasPrevious && (activeStatus === "READY" || isGenerating || isFailed)
  const showRegenerate = activeStatus === "READY" || isFailed || (isGenerating && hasPrevious)
  const showGenerate = activeStatus === "NONE" && !isActivePending
  const showFailedEmpty = isFailed && !hasPrevious && !isGenerating

  // A run starting on a folded section would render its placeholder inside a
  // closed body, so Generate looks like a no-op. Unfold on the idle → generating
  // edge only, and never on a script switch, so the folded default still holds.
  const generationRef = React.useRef({ scriptId: script.id, isGenerating })
  React.useEffect(() => {
    const previous = generationRef.current
    if (previous.scriptId === script.id && isGenerating && !previous.isGenerating) {
      setCollapsed(false)
    }
    generationRef.current = { scriptId: script.id, isGenerating }
  }, [script.id, isGenerating])

  const generateLabel =
    variant === "panels"
      ? t("opportunities.scripts.storyboard.animatic.generate", "Generate animatic")
      : t("opportunities.scripts.storyboard.generate", "Generate sketch")
  const generatingLabel =
    variant === "panels"
      ? t("opportunities.scripts.storyboard.animatic.generating", "Generating animatic…")
      : t("opportunities.scripts.storyboard.generating", "Generating sketch…")
  const sketchHelp = t(
    "opportunities.scripts.storyboard.animatic.variantSketchHelp",
    "Still sheet — every beat in one black-and-white image.",
  )
  const animaticHelp = t(
    "opportunities.scripts.storyboard.animatic.variantAnimaticHelp",
    "Motion preview — one photoreal frame per beat you can play.",
  )
  const variantHelp = variant === "panels" ? animaticHelp : sketchHelp
  const variantHelpId = React.useId()
  // Idle is header-only — no empty body. Body opens when there is media, a loader, or a failure.
  const hasBodyContent =
    (isGenerating && !hasPrevious) ||
    showPrevious ||
    showFailedEmpty ||
    (isFailed && hasPrevious && !isGenerating)
  const bodyOpen = !collapsed && hasBodyContent

  const storyboardTitle = t("opportunities.scripts.storyboard.title")
  const storyboardLabelClass =
    labelClassName ??
    "text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
  const variantReadyDot = (status: ScriptDto["storyboardStatus"]) =>
    status === "READY" ? (
      <span
        aria-hidden="true"
        className="size-1.5 shrink-0 rounded-full bg-success"
        title={t("opportunities.scripts.storyboard.variantReady", "Ready")}
      />
    ) : status === "GENERATING" ? (
      <span
        aria-hidden="true"
        className="size-1.5 shrink-0 animate-pulse rounded-full bg-warning"
      />
    ) : null

  const variantToggle = (
    <SegmentedControl
      value={variant}
      onValueChange={(next) => {
        if (next === "sketch" || next === "panels") setVariant(next)
      }}
      ariaLabel={t("opportunities.scripts.storyboard.animatic.variantLabel", "Storyboard style")}
      accent="primary"
      tone="subtle"
      options={[
        {
          value: "sketch",
          label: t("opportunities.scripts.storyboard.animatic.variantSketch", "Sketch"),
          title: sketchHelp,
          start: variantReadyDot(sketchStatus),
        },
        {
          value: "panels",
          label: t("opportunities.scripts.storyboard.animatic.variantAnimatic", "Animatic"),
          title: animaticHelp,
          start: variantReadyDot(panelStatus),
        },
      ]}
    />
  )

  // Idle: same surface as “Why this works” — muted fill, no extra nested border.
  if (showGenerate && !hasBodyContent) {
    return (
      <div
        role="group"
        aria-label={storyboardTitle}
        className="mt-4 rounded-sm bg-muted px-4 py-3.5"
      >
        <p className={storyboardLabelClass}>{storyboardTitle}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {variantToggle}
          {hideInlineSketchGenerate ? null : (
            <Button
              key={variant}
              variant="ai"
              size="sm"
              iconStart="sparkles"
              className="ml-auto border border-[var(--ai-border)] motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150 motion-reduce:animate-none"
              disabled={isPending}
              onClick={() => generate(variant)}
            >
              {generateLabel}
            </Button>
          )}
        </div>
        <p
          id={variantHelpId}
          key={`help-${variant}`}
          className="mt-2 max-w-prose text-xs leading-relaxed text-muted-foreground motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150 motion-reduce:animate-none"
        >
          {variantHelp}
        </p>
      </div>
    )
  }

  return (
    <section
      ref={sectionRef}
      aria-label={storyboardTitle}
      data-task-storyboard={script.id}
      className="mt-4 scroll-mt-6 overflow-hidden rounded-sm border border-border bg-card"
    >
      <header
        className={cn(
          "flex flex-wrap items-center gap-x-3 gap-y-2 bg-muted/20 px-4 py-2",
          bodyOpen && "border-b border-border",
        )}
      >
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={bodyOpen}
          aria-label={
            collapsed
              ? t("opportunities.scripts.storyboard.expandSection", "Show {{title}}", {
                  title: storyboardTitle,
                })
              : t("opportunities.scripts.storyboard.collapseSection", "Hide {{title}}", {
                  title: storyboardTitle,
                })
          }
          className={cn(
            storyboardLabelClass,
            "flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md px-1 text-left transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          )}
        >
          <span aria-hidden="true">{storyboardTitle}</span>
          {panelStatus === "READY" ? (
            <Badge variant="outline" className="normal-case font-normal tracking-normal">
              {t("opportunities.scripts.storyboard.hasOneAnimatic", "has 1 animatic")}
            </Badge>
          ) : null}
          <Icon
            name="chevron-down"
            size="xs"
            className={cn(
              "shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none",
              bodyOpen && "rotate-180",
            )}
            aria-hidden="true"
          />
        </button>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {variantToggle}
          {showRegenerate && !hideInlineSketchGenerate && (
            <Button
              variant="ghost"
              size="sm"
              iconStart={isActivePending ? "loader" : "refresh-cw"}
              className={cn("h-8 shrink-0", isActivePending && "[&_svg]:animate-spin")}
              disabled={isPending}
              aria-label={t("opportunities.scripts.storyboard.regenerate")}
              onClick={() => generate(variant)}
            >
              <span className="hidden sm:inline">
                {t("opportunities.scripts.storyboard.regenerate")}
              </span>
            </Button>
          )}
        </div>
      </header>

      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-in-out motion-reduce:transition-none",
          bodyOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div
            key={variant}
            aria-hidden={!bodyOpen}
            className={cn(
              "space-y-4 px-4 pb-4 pt-4",
              bodyOpen &&
                "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-200 motion-safe:fill-mode-both",
              "motion-reduce:animate-none",
            )}
          >
          {/* First generation: nothing to show yet, so a plain placeholder holds
              the space the result will occupy. A regeneration skips this — the
              previous result below carries the loading overlay instead. */}
          {isGenerating && !hasPrevious && (
            <div
              className={cn(
                "mx-auto flex w-full animate-pulse items-center justify-center gap-2 rounded-md bg-muted",
                variant === "panels" ? "aspect-[9/16] max-w-xs" : "aspect-video max-w-2xl",
              )}
              role="status"
              aria-live="polite"
            >
              <Icon name="loader" size="sm" className="animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{generatingLabel}</span>
            </div>
          )}

          {showPrevious && (
            // Both variants sit centered in the card, so the regeneration pill —
            // centered in this box — also reads as centered on the section.
            <div
              className={cn(
                "mx-auto flex w-full flex-col",
                variant === "panels" ? "max-w-xs gap-1.5" : "max-w-2xl",
              )}
            >
              <div className="relative w-full">
                <div
                  aria-hidden={isGenerating}
                  className={cn(
                    variant === "sketch" && "space-y-4",
                    isGenerating && "pointer-events-none select-none opacity-40",
                  )}
                >
                  {variant === "sketch" &&
                    images.map((img) =>
                      img.imageUrl ? (
                        <MediaPreview
                          key={img.model}
                          type="image"
                          src={img.imageUrl}
                          thumbnailSrc={img.imageUrl}
                          alt={t("opportunities.scripts.storyboard.title")}
                          className="aspect-video w-full cursor-pointer overflow-hidden rounded-md border border-border/40"
                        />
                      ) : (
                        <div
                          key={img.model}
                          className="flex aspect-video w-full items-center justify-center rounded-md border border-border/40 bg-muted text-sm text-muted-foreground"
                        >
                          {t("opportunities.scripts.storyboard.imageUnavailable")}
                        </div>
                      ),
                    )}

                  {variant === "panels" && (
                    <AnimaticPlayer
                      panels={panels}
                      scenes={script.scenes}
                      title={script.hook}
                      expanded={isAnimaticExpanded}
                      onExpandedChange={handleAnimaticExpandedChange}
                    />
                  )}
                </div>

                {/* Regeneration: the loader sits over the dimmed previous result, so
                    the user keeps their reference while the new version is produced. */}
                {isGenerating && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-md"
                  >
                    <span className="flex items-center gap-2 rounded-full bg-background/90 px-3 py-1.5 shadow-sm backdrop-blur-sm">
                      <Icon name="loader" size="sm" className="animate-spin text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{generatingLabel}</span>
                    </span>
                  </div>
                )}
              </div>

              {variant === "panels" && <StoryboardArtDirectionNote className="text-center" />}
            </div>
          )}

          {showFailedEmpty && (
            <div
              role="alert"
              className={cn(
                "mx-auto flex w-full flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border/50 bg-muted/30 px-4 py-6 text-center",
                variant === "panels" ? "aspect-[9/16] max-w-xs" : "aspect-video max-w-2xl",
              )}
            >
              <Icon name="clapperboard" size="sm" className="text-muted-foreground/35" aria-hidden="true" />
              <div className="max-w-[16rem] space-y-1">
                <p className="text-sm text-foreground/85">
                  {variant === "panels"
                    ? t(
                        "opportunities.scripts.storyboard.errorAnimatic",
                        "Animatic didn't come through",
                      )
                    : t(
                        "opportunities.scripts.storyboard.errorSketch",
                        "Sketch didn't come through",
                      )}
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {t(
                    "opportunities.scripts.storyboard.errorHint",
                    "Generation can be flaky — worth another try.",
                  )}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconStart={isActivePending ? "loader" : "refresh-cw"}
                className={cn(isActivePending && "[&_svg]:animate-spin")}
                disabled={isPending}
                onClick={() => generate(variant)}
              >
                {t("opportunities.scripts.storyboard.retry")}
              </Button>
            </div>
          )}

          {isFailed && hasPrevious && !isGenerating && (
            <p role="alert" className="text-center text-xs text-muted-foreground">
              {t(
                "opportunities.scripts.storyboard.errorKeepPrevious",
                "Latest run didn't finish — showing your previous version.",
              )}
            </p>
          )}

          </div>
        </div>
      </div>
    </section>
  )
}
